import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {IconArrowsMove} from '@tabler/icons-react';
import React, {useEffect, useMemo, useState} from 'react';
import type {AssetRequest, AssetResponse} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  ModuleEnum,
  NoteModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateAssetMutation,
  useGetAssetQuery,
  useMoveAssetMutation,
  useUpdateAssetMutation,
} from 'src/redux/queries/asset';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkAssets} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotesModule} from 'src/app/notes/NotesModule';
import {TaskDefinitionTable} from 'src/app/tasks/TaskDefinitionTable/TaskDefinitionTable';
import {
  AssetMeta,
  AssetPillConfig,
  MapAssetResponseToAssetRequest,
} from '../../assetHelpers';
import {AssetsTable} from '../../AssetList/components/AssetsTable';
import {AssetTreeModal} from './components/AssetTreeModal';
import {AssetFormSubmissionList} from './components/AssetFormSubmissionList';
import {AssetQRCodeTab} from './components/AssetQRCodeTab';
import {AssetSafetyEventList} from './components/AssetSafetyEventList';
import {useAssetNavigation} from './hooks/useAssetNavigation';
import type {AssetsViewType} from 'src/shared/constants/ViewTypes';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface AssetsFormProps {
  id?: number;
  parentId?: number;
  mode: 'create' | 'view' | 'edit';
  view?: AssetsViewType;
}

export const AssetsForm: React.FC<AssetsFormProps> = ({
  id,
  parentId,
  mode,
  view = 'overview',
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const assetPerms = useModulePermissions(ModuleEnum.Assets);
  const auditPerms = useAuditLogPermissions();

  const {skipToolboxTalks, skipActions, skipForms, skipSafetyEvents} =
    useSkipTokensByFeature();

  const [item, setItem] = useState<AssetResponse>();
  const formMeta = useMemo(() => AssetMeta(), []);
  const viewFormData = useMemo(() => toFormDataV2(item), [item]);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isSavePending, setIsSavePending] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const [createAsset] = useCreateAssetMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const [moveAsset] = useMoveAssetMutation();

  const assetId = id !== undefined && !isNaN(+id) ? +id : undefined;
  const {
    data: assetData,
    isLoading: isDataLoading,
    error: assetFetchError,
  } = useGetAssetQuery(assetId!, {skip: assetId === undefined});

  const form = useEntitySyncedForm({
    formMeta,
    initialData: {parentId, active: true, isLeaf: false},
    entity: assetData,
    onEntityArrival: setItem,
    toFormData: toFormDataV2,
  });

  useEffect(() => {
    if (assetFetchError) {
      setAlert(parseErrorMessage(assetFetchError), 'error');
    }
  }, [assetFetchError, setAlert]);

  const isLoading = isDataLoading || isUpdating;

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const body: AssetRequest = MapAssetResponseToAssetRequest(
        form.methods.getValues() as AssetResponse
      );

      const res =
        id === undefined
          ? await createAsset(body).unwrap()
          : await updateAsset(body).unwrap();

      setAlert('Item was successfully updated', 'success');

      navigate(PathUtility.AssetView(res.id, 'overview'), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    if (await form.validateForm()) {
      void handleSave();
    }
  };

  const handleMove = () => {
    setShowMoveModal(true);
  };

  const handleMoveConfirm = async (newParentId: number | null) => {
    try {
      if (!item) {
        setAlert('Asset not found.', 'error');
        return;
      }

      setIsUpdating(true);

      await moveAsset({
        id: item.id,
        newParentId: newParentId ?? 0,
      }).unwrap();

      setAlert(`${item?.name} was successfully moved.`, 'success');
      navigate(PathUtility.AssetView(item.id, 'overview'), {replace: true});
    } catch {
      setAlert('An error occurred moving the asset.', 'error');
    }

    setIsUpdating(false);
    setShowMoveModal(false);
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.AssetView(id, 'overview')
      : item?.parentId
      ? PathUtility.AssetView(item.parentId, 'children')
      : PathUtility.Assets()
  );

  const extensionTabs = useExtensionTabs(ModuleEnum.Assets, id ?? 0);

  const {RenderedTabsComponent} = useUrlQueryTabV2<AssetsViewType>({
    view: (view ?? '') as AssetsViewType,
    defaultView: 'overview',
    tabs: [
      {
        view: 'overview',
        label: 'Overview',
        component: (
          <>
            {mode !== 'view' ? (
              <>
                <ProactForm form={form}>
                  <ProactFormGrid />
                </ProactForm>
                <Group mt="md">
                  <PageHeader.Action variant="cancel" onClick={handleCancel}>
                    Cancel
                  </PageHeader.Action>
                  <PageHeader.Action
                    variant={isCreateMode ? 'create' : 'save'}
                    onClick={handleValidateAndSave}
                    loading={isSavePending}>
                    {isCreateMode ? 'Create Asset' : 'Save Changes'}
                  </PageHeader.Action>
                </Group>
              </>
            ) : (
              <FormViewComponentV2
                formData={viewFormData}
                formMeta={formMeta}
              />
            )}
          </>
        ),
      },
      {
        view: 'children',
        label: 'Child Assets',
        disabled: mode !== 'view' || item?.isLeaf === true,
        component: <AssetsTable parentId={item?.id} />,
      },
      {
        view: 'toolboxtalks',
        label: 'Toolbox Talks',
        disabled: mode !== 'view' || skipToolboxTalks,
        component: (
          <>
            {item && (
              <TaskDefinitionTable
                taskType={TaskTypeEnum.ToolboxTalk}
                filterAssetId={item.id}
                identifier={'tbt-asset-tasks-table'}
                onViewRowClick={(row: {taskType: TaskTypeEnum; id: number}) =>
                  navigate(
                    PathUtility.TaskView(row.taskType, row.id, 'content')
                  )
                }
              />
            )}
          </>
        ),
      },
      {
        view: 'actions',
        label: 'Actions',
        disabled: mode !== 'view' || skipActions,
        component: (
          <>
            {item && (
              <TaskDefinitionTable
                taskType={TaskTypeEnum.Actions}
                filterAssetId={item.id}
                identifier={'actions-asset-tasks-table'}
                onViewRowClick={(row: {id: number}) =>
                  navigate(PathUtility.ActionView(row.id, 'content'))
                }
              />
            )}
          </>
        ),
      },
      {
        view: 'assignedforms',
        label: 'Assigned Forms',
        disabled: mode !== 'view' || skipForms,
        component: (
          <>
            {item && (
              <TaskDefinitionTable
                taskType={TaskTypeEnum.Form}
                filterAssetId={item.id}
                identifier={'forms-asset-tasks-table'}
                onViewRowClick={(row: {taskType: TaskTypeEnum; id: number}) =>
                  navigate(
                    PathUtility.TaskView(row.taskType, row.id, 'content')
                  )
                }
              />
            )}
          </>
        ),
      },
      {
        view: 'forms',
        label: 'Forms',
        disabled: mode !== 'view' || skipForms,
        component: <>{item && <AssetFormSubmissionList assetId={item.id} />}</>,
      },
      {
        view: 'safety-events',
        label: 'Safety Events',
        disabled: mode !== 'view' || skipSafetyEvents,
        component: <>{item && <AssetSafetyEventList assetId={item.id} />}</>,
      },
      {
        view: 'notes',
        label: 'Notes',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <NotesModule noteType={NoteModuleEnum.Asset} entityId={item.id} />
            )}
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: mode !== 'view',
        component: (
          <>
            {item && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.Asset}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'qr-code',
        label: 'QR Code',
        disabled: mode !== 'view',
        component: <AssetQRCodeTab id={id} item={item} />,
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: id && (
          <AuditModule
            entityId={String(id)}
            entityType={EntityIdentifierEnum.Asset}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(PathUtility.AssetView(String(id), view as any), {replace: true}),
  });

  const pills = useMemo(
    () => AssetPillConfig(item?.active, item?.isLeaf),
    [item?.active, item?.isLeaf]
  );

  const navigation = useAssetNavigation({
    mode,
    id,
    itemName: item?.name,
    parentId: item?.parentId,
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Stack gap={0}>
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkAssets.icon}
            subtitle={item?.internalIdentifier}>
            {isCreateMode ? 'New Asset' : item?.name ?? 'Asset'}
          </PageHeader.Title>

          {isViewMode && item && (
            <PageHeader.Badges>
              {pills.map((pill, index) => (
                <PageHeader.Badge
                  key={index}
                  color={pill.color}
                  variant="light">
                  {pill.text}
                </PageHeader.Badge>
              ))}
            </PageHeader.Badges>
          )}

          {isEditMode && (
            <PageHeader.Badges>
              <PageHeader.Badge color="yellow" variant="light">
                Editing
              </PageHeader.Badge>
            </PageHeader.Badges>
          )}

          <PageHeader.Actions>
            {isViewMode && assetPerms.update && (
              <>
                <PageHeader.Action
                  variant="edit"
                  onClick={() =>
                    navigate(PathUtility.AssetEdit(String(id), 'overview'))
                  }>
                  Edit
                </PageHeader.Action>
                <PageHeader.Action
                  variant="secondary"
                  icon={<IconArrowsMove size={16} />}
                  onClick={handleMove}>
                  Move
                </PageHeader.Action>
              </>
            )}

            {!isViewMode && (
              <>
                <PageHeader.Action variant="cancel" onClick={handleCancel}>
                  Cancel
                </PageHeader.Action>
                <PageHeader.Action
                  variant={isCreateMode ? 'create' : 'save'}
                  onClick={handleValidateAndSave}
                  loading={isSavePending}>
                  {isCreateMode ? 'Create Asset' : 'Save Changes'}
                </PageHeader.Action>
              </>
            )}
          </PageHeader.Actions>
        </PageHeader>

        {RenderedTabsComponent}
      </Stack>

      <AssetTreeModal
        opened={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onConfirm={handleMoveConfirm}
        title="Move Asset"
        currentItemName={item?.name}
        currentItemType={item?.isLeaf ? 'leaf' : 'asset'}
        currentItemId={item?.id}
      />
    </>
  );
};
